import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { get, post } from '../../api'
import { Alert, Button, Empty, Input, Spinner, StatusChip } from '../../components/ui'
import { useAction, useLoad } from '../../hooks'
import { addDays, pluralGuests, relativeDay, shortDate, todayKey } from '../../format'
import type { Booking, DaySummary } from '../../types'
import { BookingDetailModal, NewBookingModal, quickActions } from './BookingModals'
import { useBusiness } from './Layout'

const POLL_MS = 15_000
const DONE: Booking['status'][] = ['finished', 'no_show', 'cancelled', 'expired']

export function TodayPage() {
  const { business } = useBusiness()
  const [params, setParams] = useSearchParams()
  const date = params.get('date') ?? todayKey(business.timezone)
  const setDate = (d: string) => setParams(d === todayKey(business.timezone) ? {} : { date: d }, { replace: true })
  const [query, setQuery] = useState('')
  const [showDone, setShowDone] = useState(false)
  const [creating, setCreating] = useState(false)
  const [open, setOpen] = useState<Booking | null>(null)
  const day = useLoad(() => get<{ bookings: Booking[]; summary: DaySummary }>(`/v1/admin/businesses/${business.id}/bookings?date=${date}`), [business.id, date], POLL_MS)

  const transition = useAction(async (booking: Booking, event: string) => {
    const updated = await post<Booking>(`/v1/admin/businesses/${business.id}/bookings/${booking.id}/transition`, { event })
    day.setData((prev) => (prev ? { ...prev, bookings: prev.bookings.map((b) => (b.id === updated.id ? updated : b)) } : prev))
    void day.reload({ quiet: true })
  })

  const replace = (updated: Booking) => {
    day.setData((prev) => (prev ? { ...prev, bookings: prev.bookings.map((b) => (b.id === updated.id ? updated : b)) } : prev))
    setOpen(updated)
    void day.reload({ quiet: true })
  }

  const groups = useMemo(() => {
    const all = day.data?.bookings ?? []
    const q = query.trim().toLowerCase()
    const visible = all.filter((b) => (showDone || !DONE.includes(b.status)) && (!q || b.guest.name.toLowerCase().includes(q) || b.reference.toLowerCase().includes(q) || (b.guest.phone ?? '').includes(q)))
    const periods = business.servicePeriods
    const byGroup = new Map<string, Booking[]>()
    for (const b of visible) {
      const period = periods.find((p) => b.time >= p.starts && b.time < p.ends)
      const key = period ? period.name : 'Other'
      byGroup.set(key, [...(byGroup.get(key) ?? []), b])
    }
    return [...byGroup.entries()].sort((a, b) => (a[1][0]?.time ?? '').localeCompare(b[1][0]?.time ?? ''))
  }, [day.data, query, showDone, business.servicePeriods])

  const summary = day.data?.summary
  const seatedNow = day.data?.bookings.filter((b) => b.status === 'seated').length ?? 0
  const awaiting = day.data?.bookings.filter((b) => b.status === 'awaiting_approval').length ?? 0
  const hidden = day.data ? day.data.bookings.filter((b) => DONE.includes(b.status)).length : 0
  const rel = relativeDay(date, business.timezone)

  return (
    <>
      <div className="topbar">
        <div className="day-nav">
          <Button variant="ghost" size="sm" aria-label="Previous day" onClick={() => setDate(addDays(date, -1))}>
            ‹
          </Button>
          <div className="btn btn-secondary btn-md date-btn" style={{ position: 'relative' }}>
            {rel ? `${rel} · ${shortDate(date)}` : shortDate(date)}
            <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Pick a date" />
          </div>
          <Button variant="ghost" size="sm" aria-label="Next day" onClick={() => setDate(addDays(date, 1))}>
            ›
          </Button>
          {rel !== 'Today' ? (
            <Button variant="ghost" size="sm" onClick={() => setDate(todayKey(business.timezone))}>
              Today
            </Button>
          ) : null}
        </div>
        <div className="row-flex topbar-actions">
          <Button variant="ghost" size="sm" onClick={() => window.print()} className="print-btn">
            Print
          </Button>
          <Button variant="primary" onClick={() => setCreating(true)}>
            + New booking
          </Button>
        </div>
      </div>

      {summary ? (
        <div className="stats">
          <div className="stat">
            <div className="stat-label">Covers</div>
            <div className="stat-value">
              {summary.covers} <small>of {summary.capacity}</small>
            </div>
            <div className="meter" aria-hidden="true">
              <span style={{ width: `${Math.min(100, summary.capacity ? (summary.covers / summary.capacity) * 100 : 0)}%` }} />
            </div>
          </div>
          <div className="stat">
            <div className="stat-label">Bookings</div>
            <div className="stat-value">{summary.bookings}</div>
          </div>
          <div className="stat">
            <div className="stat-label">Seated now</div>
            <div className="stat-value">{seatedNow}</div>
          </div>
          {awaiting ? (
            <div className="stat" style={{ borderColor: 'var(--warn)' }}>
              <div className="stat-label">Needs approval</div>
              <div className="stat-value">{awaiting}</div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="row-flex" style={{ marginBottom: 14 }}>
        <div className="search grow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <Input placeholder="Find by name, phone or reference" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Find a booking" />
        </div>
        {hidden ? (
          <Button variant="ghost" size="sm" onClick={() => setShowDone(!showDone)}>
            {showDone ? 'Hide' : 'Show'} {hidden} finished
          </Button>
        ) : null}
      </div>

      {transition.error ? <Alert>{transition.error}</Alert> : null}
      {day.error && !day.data ? <Alert>{day.error}</Alert> : null}
      {day.loading && !day.data ? <Spinner /> : null}
      {day.data && groups.length === 0 ? (
        <Empty title={query ? 'No bookings match' : 'No bookings yet'}>
          {query ? 'Try a different name or clear the search.' : 'Bookings from the website appear here as they are confirmed. Take a phone booking with the button above.'}
        </Empty>
      ) : null}

      <div className="timeline">
        {groups.map(([name, list]) => (
          <section className="slot-group" key={name}>
            <h3>
              <span>{name}</span>
              <span>{list.reduce((s, b) => (DONE.includes(b.status) ? s : s + b.partySize), 0)} covers</span>
            </h3>
            <div className="booking-list">
              {list.map((b) => (
                <BookingRow key={b.id} booking={b} onOpen={() => setOpen(b)} onAction={(event) => void transition.run(b, event)} busy={transition.busy} />
              ))}
            </div>
          </section>
        ))}
      </div>

      <Button variant="primary" className="fab" onClick={() => setCreating(true)} aria-label="New booking">
        + New
      </Button>

      {creating ? (
        <NewBookingModal
          initialDate={date}
          onClose={() => setCreating(false)}
          onCreated={(b) => {
            setCreating(false)
            if (b.date === date) day.setData((prev) => (prev ? { ...prev, bookings: [...prev.bookings, b].sort((x, y) => x.startsAt.localeCompare(y.startsAt)) } : prev))
            else setDate(b.date)
            void day.reload({ quiet: true })
          }}
        />
      ) : null}
      {open ? <BookingDetailModal booking={open} onClose={() => setOpen(null)} onChange={replace} /> : null}
    </>
  )
}

export function BookingRow({ booking: b, onOpen, onAction, busy }: { booking: Booking; onOpen: () => void; onAction?: (event: string) => void; busy?: boolean }) {
  const actions = onAction ? quickActions(b.status) : []
  const done = DONE.includes(b.status)
  return (
    <div className={`booking-row ${done ? 'is-done' : ''}`} role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen())}>
      <div>
        <div className="time">{b.time}</div>
        <div className="party">{pluralGuests(b.partySize)}</div>
      </div>
      <div className="who">
        <div className="name">{b.guest.name}</div>
        <div className="meta">
          {[b.areaName, b.guest.phone, b.source === 'widget' || b.source === 'hosted' ? 'online' : b.source.replace('_', '-')].filter(Boolean).join(' · ')}
        </div>
        {b.occasion || b.specialRequests || b.internalNotes ? (
          <div className="flags">
            {b.occasion ? <span className="flag">{b.occasion}</span> : null}
            {b.specialRequests ? <span className="flag">{b.specialRequests.length > 40 ? `${b.specialRequests.slice(0, 40)}…` : b.specialRequests}</span> : null}
            {b.internalNotes ? <span className="flag flag-note">{b.internalNotes.length > 40 ? `${b.internalNotes.slice(0, 40)}…` : b.internalNotes}</span> : null}
          </div>
        ) : null}
      </div>
      <div className="actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <StatusChip status={b.status} />
        {actions.length ? (
          <div className="quick-actions">
            {actions.map((a) => (
              <Button key={a.event} size="sm" variant={a.variant === 'primary' ? 'primary' : a.variant === 'danger' ? 'danger' : 'secondary'} disabled={busy} onClick={() => onAction?.(a.event)}>
                {a.label}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}
